import { inject, Injectable, Injector } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { DashboardCartAndSettings } from '@cloud-twin-v2/cloud-twin-shared/filtrable-items/dashboard-cart-and-settings';
import { SelectedWorkspaceService } from '@cloud-twin-v2/cloud-twin-shared/selected-workspace.service';
import { CtWorkspaceDto } from '@cloud-twin-v2/generated/simulation.generated.client';
import { DynamicLayoutBuilderService } from '@common-modules/dynamic-layout/layouts/dynamic-layout-builder-service';
import { SimpleDynamicLayoutSettings } from '@common-modules/dynamic-layout/layouts/simple-dynamic-layout-settings';
import { SimpleDynamicLayoutSettingsItem } from '@common-modules/dynamic-layout/layouts/simple-dynamic-layout-settings-item';
import { DynamicLayoutItemSettings } from '@common-modules/dynamic-layout/models/dynamic-layout-item-settings';
import { DynamicLayoutSettings } from '@common-modules/dynamic-layout/models/dynamic-layout-settings';
import { ReduxStateService } from '@common-modules/redux/redux-state.service';
import { DashboardDefinition } from '@common-modules/right-panel/dashboards/dashboard-definition';
import { DashboardGroupDefinition } from '@common-modules/right-panel/dashboards/dashboard-group-definition';
import { DashboardIdentity } from '@common-modules/right-panel/dashboards/dashboard-identity';
import { DashboardChildWidgetParams } from '@common-modules/right-panel/dashboards/dashboard-widget-params';
import { DashboardsService } from '@common-modules/right-panel/dashboards/dashboards.service';
import { AppModules } from '@common-modules/shared/app-modules.enum';
import { PermissionsRequest } from '@common-modules/shared/auth/models/permissions-request';
import { AuthenticationService } from '@common-modules/shared/auth/services/authentication.service';
import { DialogService } from '@common-modules/shared/dialogs/dialogs.service';
import { DateHelperService } from '@common-modules/shared/helpers/date-helper.service';
import { LocalizationHelperService } from '@common-modules/shared/localization/localization-helper.service';
import { LogService } from '@common-modules/shared/wlm-log/log.service';
import { filter, map, Observable, of, switchMap, take } from 'rxjs';
import { CreateDashboardWidgetPopupComponent } from './create-dashboard-widget-popup/create-dashboard-widget-popup.component';
import { DashboardWidgetTypes } from './models/dashboard-widget-types';
import { WorkspaceDashboardsSettings } from './workspace-dashboards-settings';

class WorkspaceDashboardsSettingApiDto {
  workspaceDashboardsId: string;
  workspaceId: string;
  definition: string;
  userCode: string;
  lastUpdate: string;
}

class DashboardDefinitionApiDto {
  dashboardDefinitionId: string;
  title: string;
  definition: string;
  userCode: string;
  lastUpdate: string;
}

@Injectable()
export class CtDashboardsService extends DashboardsService {
  private readonly _dialogService = inject(DialogService);
  private readonly _selectedWorkspaceService = inject(SelectedWorkspaceService);
  private readonly _dynamicLayoutBuilderService = inject(DynamicLayoutBuilderService);
  private readonly _authenticationService = inject(AuthenticationService);
  private readonly _dateHelper = inject(DateHelperService);
  private readonly _logService = inject(LogService);
  private readonly _localization = inject(LocalizationHelperService);
  private readonly _state = inject(ReduxStateService);

  private readonly _dashboardGroupStateModule = 'DashboardGroupModule';
  private readonly _dashboardGroupStatePage = 'DashboardGroupPage';
  private readonly _dashboardStateModule = 'DashboardModule';
  private readonly _dashboardStatePage = 'DashboardPage';
  private readonly _defaultWidgetTitleKeys = `${AppModules.RightPanel}.ct-dashboard.widgets`;

  private readonly _widgetComponentByType = {
    [DashboardWidgetTypes.Kpi]: 'DashboardKpiWidgetComponent',
    [DashboardWidgetTypes.AttributeCategory]: 'DashboardAttibuteCategoryWidgetComponent',
    [DashboardWidgetTypes.AttributeChart]: 'DashboardChartWidgetComponent',
    [DashboardWidgetTypes.Grid]: 'DashboardGridWidgetComponent',
    [DashboardWidgetTypes.Text]: 'DashboardTextWidgetComponent',
  };

  get entityUrl(): string {
    return `${this.apiUrl}/workspace/dashboard`;
  }

  constructor(injector: Injector) {
    super(injector);
  }

  loadGroup(): Observable<DashboardGroupDefinition> {
    return this.selectedWorkspace$.pipe(
      switchMap((workspace) => {
        return this.loadGroupFromApi(workspace.workspaceId).pipe(
          switchMap((dashboardGroup) => {
            if (dashboardGroup) {
              return of(dashboardGroup);
            } else {
              return this.createDefaultDashboardGroup(workspace.workspaceId).pipe(
                switchMap((defaultDashboardGroup) => this.createGroup(defaultDashboardGroup))
              );
            }
          })
        );
      })
    );
  }

  createGroup(dashboardGroup: DashboardGroupDefinition): Observable<DashboardGroupDefinition> {
    return this.selectedWorkspace$.pipe(
      switchMap((workspace) => {
        const workspaceDashboardSettings: WorkspaceDashboardsSettings = {
          ...dashboardGroup,
          workspaceId: workspace.workspaceId,
        };
        return this.createGroupToApi(workspaceDashboardSettings);
      })
    );
  }

  updateGroup(dashboardGroup: DashboardGroupDefinition): Observable<DashboardGroupDefinition> {
    return this.selectedWorkspace$.pipe(
      switchMap((workspace) => {
        const workspaceDashboardSettings: WorkspaceDashboardsSettings = {
          ...dashboardGroup,
          workspaceId: workspace.workspaceId,
        };
        return this.updateGroupToApi(workspaceDashboardSettings);
      })
    );
  }

  createDashboard(dashboard: DashboardDefinition): Observable<DashboardDefinition> {
    return this.createEmptyDashboardLayout().pipe(
      switchMap((emptyLayoutSettings) => {
        dashboard.dynamicLayoutSettings = emptyLayoutSettings;
        const apiDashboard = this.mapDashboardToApi(dashboard);
        return this.httpClient
          .post<DashboardDefinitionApiDto>(`${this.entityUrl}/definition`, apiDashboard)
          .pipe(map((apiResponse) => this.mapApiToDashboard(apiResponse)));
      })
    );
  }

  updateDashboard(dashboard: DashboardDefinition): Observable<DashboardDefinition> {
    this._logService.info({ msg: `Update dashboard with id ${dashboard.id}`, payload: dashboard });

    const apiDashboard = this.mapDashboardToApi(dashboard);
    return this.httpClient
      .put<DashboardDefinitionApiDto>(`${this.entityUrl}/definition`, apiDashboard)
      .pipe(map((apiResponse) => this.mapApiToDashboard(apiResponse)));
  }

  loadDashboard(dashboard: DashboardIdentity): Observable<DashboardDefinition> {
    return this.httpCacheClient
      .get<DashboardDefinitionApiDto>(
        `${this.entityUrl}/definition`,
        { avoid: true },
        {
          dashboardId: dashboard.id,
        }
      )
      .pipe(map((apiResponse) => this.mapApiToDashboard(apiResponse)));
  }

  mapDashboardGroupToLayout(dashboardGroup: WorkspaceDashboardsSettings): DynamicLayoutSettings {
    dashboardGroup.dynamicLayoutSettings.params = {
      dashboardGroup: {
        ...dashboardGroup,
        dynamicLayoutSettings: null,
      },
    };

    return dashboardGroup.dynamicLayoutSettings;
  }

  mapLayoutToDashboardGroup(
    dynamicLayoutSettings: DynamicLayoutSettings
  ): DashboardGroupDefinition {
    const groupDefinition: DashboardGroupDefinition = {
      ...(dynamicLayoutSettings?.params?.dashboardGroup || {}),
      dynamicLayoutSettings,
    };
    return groupDefinition;
  }

  createDashboardChildWidget(): Observable<any> {
    const config = new MatDialogConfig();
    config.width = '80vw';
    config.height = '90vh';
    const popupRef = this._dialogService.openComponent(CreateDashboardWidgetPopupComponent, config);

    return popupRef.afterClosed().pipe(
      switchMap((cartSelection) => {
        if (!cartSelection) {
          return of(null);
        }
        return this.buildChildWidget(cartSelection);
      })
    );
  }

  private buildChildWidget(
    cartAndSettings: DashboardCartAndSettings
  ): Observable<DynamicLayoutItemSettings> {
    return this._localization.get(this._defaultWidgetTitleKeys).pipe(
      switchMap((widgetTranslations) => {
        const title = widgetTranslations[cartAndSettings.widgetType];

        const widgetName = this._widgetComponentByType[cartAndSettings.widgetType];

        if (!widgetName) {
          this._logService.error({
            msg: `No widget was defined for the widget type ${cartAndSettings.widgetType}.`,
          });
          return;
        }

        const simpleItem: SimpleDynamicLayoutSettingsItem = {
          widgetName,
          title,
          params: cartAndSettings as DashboardChildWidgetParams,
        };

        return this._dynamicLayoutBuilderService.buildSingleItem(simpleItem, {
          generateWidgetIds: true,
        });
      })
    );
  }

  getDashboardsGroupPermissions(): Observable<PermissionsRequest> {
    return this._selectedWorkspaceService.isCurrentWorkspaceOwnerOrSuperUser$.pipe(
      map((currentUserIsOwner) => {
        const permissions: PermissionsRequest = {
          canCreate: currentUserIsOwner,
          canUpdate: currentUserIsOwner,
          canDelete: currentUserIsOwner,
          canRead: true,
        };

        return permissions;
      })
    );
  }

  private loadGroupFromApi(workspaceId: string): Observable<WorkspaceDashboardsSettings> {
    return this.httpCacheClient
      .get<WorkspaceDashboardsSettingApiDto>(this.entityUrl, { avoid: true }, { workspaceId })
      .pipe(map((apiSettings) => this.mapApiToGroup(apiSettings)));
  }

  private createGroupToApi(
    groupDefinition: WorkspaceDashboardsSettings
  ): Observable<DashboardGroupDefinition> {
    const apiSettings = this.mapGroupToApi(groupDefinition);
    return this.httpClient
      .post<WorkspaceDashboardsSettingApiDto>(this.entityUrl, apiSettings)
      .pipe(map((resultSettings) => this.mapApiToGroup(resultSettings)));
  }

  private updateGroupToApi(
    groupDefinition: WorkspaceDashboardsSettings
  ): Observable<DashboardGroupDefinition> {
    const apiSettings = this.mapGroupToApi(groupDefinition);
    return this.httpClient
      .put<WorkspaceDashboardsSettingApiDto>(this.entityUrl, apiSettings)
      .pipe(map((resultSettings) => this.mapApiToGroup(resultSettings)));
  }

  private mapGroupToApi(settings: WorkspaceDashboardsSettings): WorkspaceDashboardsSettingApiDto {
    const apiSettings: WorkspaceDashboardsSettingApiDto = {
      workspaceDashboardsId: settings.id,
      workspaceId: settings.workspaceId,
      definition: JSON.stringify(settings.dynamicLayoutSettings),
      userCode: settings.userCode,
      lastUpdate: this.toApiFormat(settings.lastUpdate),
    };
    return apiSettings;
  }

  private mapApiToGroup(
    apiSettings: WorkspaceDashboardsSettingApiDto
  ): WorkspaceDashboardsSettings | null {
    if (!apiSettings) {
      return null;
    }

    const dynamicLayoutSettings: DynamicLayoutSettings = JSON.parse(apiSettings.definition);
    const settings: WorkspaceDashboardsSettings = {
      id: apiSettings.workspaceDashboardsId,
      workspaceId: apiSettings.workspaceId,
      dynamicLayoutSettings,
      userCode: apiSettings.userCode,
      lastUpdate: new Date(apiSettings.lastUpdate),
    };
    return settings;
  }

  private mapDashboardToApi(dashboard: DashboardDefinition): DashboardDefinitionApiDto {
    const apiSettings: DashboardDefinitionApiDto = {
      dashboardDefinitionId: dashboard.id,
      title: dashboard.title,
      definition: JSON.stringify(dashboard.dynamicLayoutSettings),
      userCode: dashboard.userCode,
      lastUpdate: this._dateHelper.toApiFormat(dashboard.lastUpdate),
    };
    return apiSettings;
  }

  private mapApiToDashboard(apiSettings: DashboardDefinitionApiDto): DashboardDefinition | null {
    if (!apiSettings) {
      return null;
    }

    const dynamicLayoutSettings: DynamicLayoutSettings = JSON.parse(apiSettings.definition);
    const settings: DashboardDefinition = {
      id: apiSettings.dashboardDefinitionId,
      title: apiSettings.title,
      dynamicLayoutSettings,
      userCode: apiSettings.userCode,
      lastUpdate: new Date(apiSettings.lastUpdate),
    };
    return settings;
  }

  private createDefaultDashboardGroup(workspaceId: string): Observable<DashboardGroupDefinition> {
    return this.createEmptyDashboardGroupLayout().pipe(
      map((dynamicLayoutSettings) => {
        const dashboardGroup: WorkspaceDashboardsSettings = {
          id: null,
          dynamicLayoutSettings,
          workspaceId,
          userCode: this._authenticationService.userCode,
          lastUpdate: null,
        };

        return dashboardGroup;
      })
    );
  }

  private get selectedWorkspace$(): Observable<CtWorkspaceDto> {
    return this._selectedWorkspaceService.selectedWorkspace$.pipe(
      filter((workspace) => Boolean(workspace)),
      take(1)
    );
  }

  private createEmptyLayout(
    widgetModule: string,
    widgetPage: string
  ): Observable<DynamicLayoutSettings> {
    const simpleSettings: SimpleDynamicLayoutSettings = {
      layoutArea: widgetModule,
      layoutKey: widgetPage,
      items: [],
    };

    return this._dynamicLayoutBuilderService.build(simpleSettings).pipe(
      map((settings: DynamicLayoutSettings) => {
        settings.showCloseWidgetIcon = true;
        // Although all pages have different persistency keys, they all have the same redux path.
        settings.widgetModule = widgetModule;
        settings.widgetPage = widgetPage;

        return settings;
      })
    );
  }

  private createEmptyDashboardGroupLayout(): Observable<DynamicLayoutSettings> {
    return this.createEmptyLayout(this._dashboardGroupStateModule, this._dashboardGroupStatePage);
  }

  private createEmptyDashboardLayout(): Observable<DynamicLayoutSettings> {
    return this.createEmptyLayout(this._dashboardStateModule, this._dashboardStatePage);
  }

  private toApiFormat = (date) =>
    typeof date === 'string' ? date : this._dateHelper.toApiFormat(date);
}
